import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tasksService, completionsService, rewardsService, childrenService, challengesService } from '../services';
import Notifications from '../components/Notifications';
import EventsCalendarTab from '../components/EventsCalendarTab';
import './ChildDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function ChildDashboard() {
  const [tasks, setTasks] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [childData, setChildData] = useState(null);
  const [badgeInfo, setBadgeInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('tasks');
  const [showNewChallengeForm, setShowNewChallengeForm] = useState(false);
  const [newChallenge, setNewChallenge] = useState({ title: '', description: '', points: 50 });
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Lê query params e muda de aba automaticamente
    const tabParam = searchParams.get('tab');
    if (tabParam && ['tasks', 'challenges', 'rewards', 'history', 'events'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      // Carrega dados da criança
      const profileResp = await childrenService.getMyProfile();
      setChildData(profileResp.data);

      try {
        const badgeResp = await childrenService.getMyBadges();
        setBadgeInfo(badgeResp.data);
      } catch (badgeErr) {
        console.warn('Erro ao carregar badges:', badgeErr);
        // Não bloqueia o carregamento se badges falharem
      }

      const tasksResp = await tasksService.getTodayTasks();
      setTasks(tasksResp.data.tasks);

      const rewardsResp = await rewardsService.getRewards();
      setRewards(rewardsResp.data.rewards);

      // Carrega resgates
      const redemptionsResp = await rewardsService.getMyRedemptions();
      setRedemptions(redemptionsResp.data.redemptions);

      // Carrega desafios
      loadChallenges();
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error('Erro:', err);
    }
  };

  const loadChallenges = async () => {
    try {
      const response = await challengesService.getChallenges();
      setChallenges(response.data.challenges);
    } catch (err) {
      console.error('Erro ao carregar desafios:', err);
    }
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    try {
      const childId = localStorage.getItem('user_id');
      if (!childId) {
        alert('Erro: Sessão expirada. Faça login novamente.');
        return;
      }
      
      await challengesService.createChallenge(childId, newChallenge.title, newChallenge.description, parseInt(newChallenge.points, 10));
      setNewChallenge({ title: '', description: '', points: 50 });
      setShowNewChallengeForm(false);
      await loadChallenges();
      alert('Desafio enviado para aprovação!');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Erro ao criar desafio';
      console.error('Erro ao criar desafio:', err);
      alert(errorMsg);
    }
  };

  const handleAcceptChallenge = async (challengeId) => {
    try {
      await challengesService.acceptChallenge(challengeId);
      await loadChallenges();
      await loadData();
      alert('Desafio aceito! Você pode cumprir a tarefa agora.');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao aceitar desafio');
    }
  };

  const handleRejectChallenge = async (challengeId) => {
    if (!window.confirm('Tem certeza que deseja rejeitar este desafio?')) return;
    try {
      await challengesService.rejectChallenge(challengeId);
      await loadChallenges();
      alert('Desafio rejeitado.');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao rejeitar desafio');
    }
  };

  const handleCancelChallenge = async (challengeId) => {
    if (!window.confirm('Tem certeza que deseja cancelar este desafio?')) return;
    try {
      await challengesService.cancelChallenge(challengeId);
      await loadChallenges();
      alert('Desafio cancelado.');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao cancelar desafio');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/child-login');
  };

  // eslint-disable-next-line no-unused-vars
  const handleStartStudyMission = useCallback(async (taskId) => {
    console.log('🚀 [ChildDashboard] handleStartStudyMission iniciado');
    console.log('   taskId:', taskId);
    console.log('   API_URL:', API_URL);
    
    try {
      // Busca o study_mission_id pelo task_id
      const token = localStorage.getItem('access_token');
      console.log('   Token:', token ? `${token.substring(0, 20)}...` : 'NULL');
      
      const url = `${API_URL}/study-missions/by-task/${taskId}`;
      console.log('   URL completa:', url);
      
      console.log('🌐 Fazendo fetch...');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Response status:', response.status);
      const data = await response.json();
      console.log('   Data:', data);
      
      if (data.study_mission) {
        console.log('✅ Study mission encontrada, navegando para:', `/child/study-missions/${data.study_mission.id}`);
        navigate(`/child/study-missions/${data.study_mission.id}`);
      } else {
        console.warn('⚠️  Study mission não encontrada no response');
        alert('Missão de estudo não encontrada');
      }
    } catch (err) {
      console.error('❌ Erro ao abrir missão:', err);
      console.error('   Message:', err.message);
      console.error('   Stack:', err.stack);
      alert('Erro ao abrir missão de estudo');
    }
  }, [navigate]);
  

  return (
    <div className="child-dashboard">
      <div className="child-header-bar">
        <h1>🦸 Minhas Missões</h1>
        <div className="header-right">
          {childData && (
            <div className="points-display">
              <span className="points-label">Minhas HeroCoins:</span>
              <span className="points-value">⭐ {childData.available_points}</span>
              <Notifications />
            </div>
          )}
          {badgeInfo && childData && (
            <div className="badge-chip" title={`${childData.name} - Semanas seguidas: ${badgeInfo.consecutive_success_weeks}`}>
              <span className="badge-level">Nível {badgeInfo.current_badge.level}</span>
              <span className="badge-name">{childData.name}</span>
              <span className="badge-rank">{badgeInfo.current_badge.name}</span>
              {badgeInfo.weeks_to_next !== null && badgeInfo.next_badge && (
                <span className="badge-progress">Próx.: {badgeInfo.next_badge.name} em {badgeInfo.weeks_to_next} semana(s)</span>
              )}
            </div>
          )}
          <button className="logout-btn" onClick={handleLogout}>
            Sair
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="child-tabs">
        <button
          className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
          data-icon="📋"
          aria-label="Tarefas do dia"
        >
          Tarefas
        </button>
        <button
          className={`tab-btn ${activeTab === 'challenges' ? 'active' : ''}`}
          onClick={() => setActiveTab('challenges')}
          data-icon="⚔️"
          aria-label="Desafios"
        >
          Desafios
        </button>
        <button
          className={`tab-btn ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
          data-icon="🎁"
          aria-label="Loja de recompensas"
        >
          Loja
        </button>
        <button
          className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
          data-icon="📅"
          aria-label="Compromissos"
        >
          Compromissos
        </button>
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
          data-icon="📜"
          aria-label="Histórico de resgates"
        >
          Histórico
        </button>
      </div>

      {activeTab === 'tasks' && <TasksTab tasks={tasks} onTaskComplete={loadData} />}
      
      {activeTab === 'challenges' && (
        <div className="tab-content">
          <button className="add-task-btn" onClick={() => setShowNewChallengeForm(!showNewChallengeForm)}>
            ⚔️ Propor Novo Desafio
          </button>

          {showNewChallengeForm && (
            <form onSubmit={handleCreateChallenge} className="task-form">
              <input
                type="text"
                placeholder="O que você quer fazer?"
                required
                value={newChallenge.title}
                onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })}
              />
              <textarea
                placeholder="Descreva o desafio (opcional)"
                value={newChallenge.description}
                onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })}
              ></textarea>
              <input
                type="number"
                placeholder="Quantos HeroCoins?"
                value={newChallenge.points}
                onChange={(e) => setNewChallenge({ ...newChallenge, points: e.target.value })}
                min="1"
                max="300"
                required
              />
              <div className="form-actions">
                <button type="submit">Enviar Proposta</button>
                <button type="button" onClick={() => setShowNewChallengeForm(false)}>Cancelar</button>
              </div>
            </form>
          )}

          <div className="challenges-section">
            <h3>📥 Desafios Pendentes</h3>
            <div className="challenges-list">
              {challenges.filter(c => c.created_by_role === 'PARENT' && c.status === 'SENT').length > 0 ? (
                challenges.filter(c => c.created_by_role === 'PARENT' && c.status === 'SENT').map((challenge) => (
                  <div key={challenge.id} className="challenge-card">
                    <div className="challenge-header">
                      <h4>{challenge.title}</h4>
                      <span className="challenge-points">⭐ {challenge.points} HeroCoins</span>
                    </div>
                    {challenge.description && <p className="challenge-description">{challenge.description}</p>}
                    <div className="challenge-meta">
                      <span>📅 {new Date(challenge.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <div className="challenge-actions">
                      <button className="accept-btn" onClick={() => handleAcceptChallenge(challenge.id)}>✅ Aceitar</button>
                      <button className="reject-btn" onClick={() => handleRejectChallenge(challenge.id)}>❌ Rejeitar</button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty">Nenhum desafio pendente</p>
              )}
            </div>
          </div>

          <div className="challenges-section">
            <h3>📤 Minhas Propostas</h3>
            <div className="challenges-list">
              {challenges.filter(c => c.created_by_role === 'CHILD').length > 0 ? (
                challenges.filter(c => c.created_by_role === 'CHILD').map((challenge) => (
                  <div key={challenge.id} className="challenge-card">
                    <div className="challenge-header">
                      <h4>{challenge.title}</h4>
                      <span className="challenge-points">⭐ {challenge.points} HeroCoins</span>
                    </div>
                    {challenge.description && <p className="challenge-description">{challenge.description}</p>}
                    <div className="challenge-meta">
                      <span>📅 {new Date(challenge.created_at).toLocaleDateString('pt-BR')}</span>
                      <span className={`challenge-status status-${challenge.status.toLowerCase()}`}>
                        {challenge.status === 'SENT' && '⏳ Aguardando'}
                        {challenge.status === 'ACCEPTED' && '✅ Aceito'}
                        {challenge.status === 'REJECTED' && '❌ Rejeitado'}
                        {challenge.status === 'CANCELLED' && '🚫 Cancelado'}
                      </span>
                    </div>
                    {challenge.status === 'SENT' && (
                      <button className="cancel-btn" onClick={() => handleCancelChallenge(challenge.id)}>Cancelar</button>
                    )}
                  </div>
                ))
              ) : (
                <p className="empty">Você ainda não propôs nenhum desafio</p>
              )}
            </div>
          </div>
        </div>
      )}
      {activeTab === 'rewards' && <RewardsTab rewards={rewards} onRedeem={loadData} />}
      {activeTab === 'events' && (
        <div className="tab-content">
          <EventsCalendarTab
            mode="child"
            childId={childData?.id}
            onViewTask={() => setActiveTab('tasks')}
            onViewStudyMission={(missionId) => navigate(`/child/study-missions/${missionId}`)}
          />
        </div>
      )}
      {activeTab === 'history' && <HistoryTab redemptions={redemptions} onConfirm={loadData} />}
    </div>
  );
}

function TasksTab({ tasks, onTaskComplete }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskFilter, setTaskFilter] = useState('pending');

  const handleSubmitTask = async (taskId) => {
    const fileInput = document.getElementById(`file-${taskId}`);
    
    if (!fileInput || !fileInput.files || !fileInput.files[0]) {
      // Se não houver arquivo selecionado, abre o seletor de arquivo
      if (fileInput) {
        fileInput.click();
      }
      return;
    }

    setLoading(true);
    setError('');

    try {
      const file = fileInput.files[0];
      await completionsService.createCompletion(taskId, file);
      alert('✅ Tarefa enviada! Aguarde a revisão do responsável.');
      fileInput.value = '';
      setSelectedTaskId(null);
      onTaskComplete();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar tarefa');
      console.error('Erro ao enviar tarefa:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelected = (taskId) => {
    setSelectedTaskId(taskId);
  };

  const visibleTasks = taskFilter === 'all'
    ? tasks
    : tasks.filter((task) => !task.completed);

  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}

      <div className="task-filter-bar">
        <span className="task-filter-label">Mostrar</span>
        <select
          className="task-filter-select"
          value={taskFilter}
          onChange={(e) => setTaskFilter(e.target.value)}
          aria-label="Filtrar tarefas"
        >
          <option value="pending">Somente pendentes</option>
          <option value="all">Pendentes e finalizadas</option>
        </select>
      </div>

      <div className="tasks-grid">
        {visibleTasks.length > 0 ? (
          /* eslint-disable no-undef */
          visibleTasks.map((task) => {
            // Verifica se a tarefa foi concluída no período
            const isCompleted = task.completed === true;
            const hasPendingSubmission = task.pending_submission !== null && task.pending_submission !== undefined;
            const isRejected = hasPendingSubmission && (
              task.pending_submission.status === 'REJECTED' || 
              task.pending_submission.status === 'REJECTED_PARENT' || 
              task.pending_submission.status === 'REJECTED_AI'
            );
            // Pode submeter se está disponível OU se foi rejeitada (pode reenviar)
            const canSubmit = task.is_available === true || isRejected;
            
            // Status da tarefa
            let statusInfo;
            if (isCompleted) {
              statusInfo = { emoji: '✅', text: 'Concluída', color: '#4CAF50' };
            } else if (hasPendingSubmission) {
              const pendingStatus = task.pending_submission.status;
              if (pendingStatus === 'PENDING' || pendingStatus === 'PENDING_PARENT' || pendingStatus === 'PENDING_AI') {
                statusInfo = { emoji: '⏳', text: 'Aguardando análise', color: '#FF9800' };
              } else if (pendingStatus === 'REJECTED' || pendingStatus === 'REJECTED_PARENT' || pendingStatus === 'REJECTED_AI') {
                statusInfo = { emoji: '❌', text: 'Rejeitada', color: '#F44336' };
              } else {
                statusInfo = { emoji: '📝', text: 'Disponível', color: '#2196F3' };
              }
            } else {
              statusInfo = { emoji: '📝', text: 'Disponível', color: '#2196F3' };
            }

            // Define o badge de frequência
            const frequencyBadge = {
              'DAILY': { text: '📅 Diária', color: '#667eea' },
              'WEEKLY': { text: '📆 Semanal', color: '#f59e0b' },
              'ONE_TIME': { text: '🎯 Única', color: '#10b981' }
            }[task.frequency] || { text: task.frequency, color: '#6b7280' };

            return (
              <div key={task.id} className={`task-item ${isCompleted ? 'completed' : ''}`}>
                <div className="task-header-row">
                  <div className="task-icon">
                    {task.category === 'CASA' && '🏠'}
                    {task.category === 'ESTUDOS' && '📚'}
                    {task.category === 'HIGIENE' && '🧴'}
                    {task.category === 'RESPONSABILIDADE' && '⚡'}
                    {task.category === 'OUTROS' && '✨'}
                  </div>
                  <span 
                    className="frequency-badge" 
                    style={{ backgroundColor: frequencyBadge.color }}
                    title={
                      task.frequency === 'DAILY' ? 'Esta tarefa reseta todo dia' :
                      task.frequency === 'WEEKLY' ? 'Você tem a semana toda (segunda a domingo) para completar' :
                      'Tarefa única - só precisa fazer uma vez'
                    }
                  >
                    {frequencyBadge.text}
                  </span>
                </div>

                <h3>{task.title}</h3>
                <p>{task.description}</p>

                <div className="task-reward">
                  <span className="points">⭐ {task.points} HeroCoins</span>
                </div>

                <div className="task-status" style={{ borderLeftColor: statusInfo.color }}>
                  <span className="status-badge" style={{ backgroundColor: statusInfo.color }}>
                    {statusInfo.emoji} {statusInfo.text}
                  </span>

                  {isCompleted && task.completion && (
                    <div className="completion-info">
                      <p className="completion-message">🎉 Parabéns! Você ganhou {task.completion.points_awarded || task.points} HeroCoins!</p>
                      {task.completion.parent_comment && (
                        <div className="parent-feedback">
                          <strong>💬 Feedback do responsável:</strong>
                          <p>{task.completion.parent_comment}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {hasPendingSubmission && (
                    task.pending_submission.status === 'REJECTED' || 
                    task.pending_submission.status === 'REJECTED_PARENT' || 
                    task.pending_submission.status === 'REJECTED_AI'
                  ) && (
                    <div className="rejection-note">
                      <p className="rejection-title">❌ Comprovante rejeitado.</p>
                      {task.pending_submission.parent_comment && (
                        <p className="rejection-comment">💬 Feedback: {task.pending_submission.parent_comment}</p>
                      )}
                      {task.pending_submission.ai_comment && !task.pending_submission.parent_comment && (
                        <p className="rejection-comment">🤖 Análise da IA: {task.pending_submission.ai_comment}</p>
                      )}
                      <p className="rejection-action">📸 Envie uma nova foto para tentar novamente.</p>
                    </div>
                  )}
                </div>

                {/* Botão especial para Missões de Estudo */}
                {task.type === 'STUDY_MISSION' && !isCompleted && (
                  <button
                    className="study-mission-btn"
                    onClick={async () => {
                      console.log('🚀 [Botão Iniciar] Clicado para task:', task.id);
                      try {
                        const token = localStorage.getItem('access_token');
                        const url = `${API_URL}/study-missions/by-task/${task.id}`;
                        console.log('   URL:', url);
                        const response = await fetch(url, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        console.log('   Status:', response.status);
                        const data = await response.json();
                        console.log('   Data:', data);
                        if (data.study_mission) {
                          console.log('✅ Navegando para:', `/child/study-missions/${data.study_mission.id}`);
                          navigate(`/child/study-missions/${data.study_mission.id}`);
                        } else {
                          console.warn('⚠️  Missão não encontrada');
                          alert('Missão de estudo não encontrada');
                        }
                      } catch (err) {
                        console.error('❌ Erro ao abrir missão:', err);
                        alert('Erro ao abrir missão de estudo');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#6a5acd',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      marginTop: '10px',
                      transition: 'all 0.3s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#5a4acd'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#6a5acd'}
                  >
                    📘 Iniciar Missão de Estudo
                  </button>
                )}

                {/* Botão para visualizar missão concluída */}
                {task.type === 'STUDY_MISSION' && isCompleted && (
                  <button
                    className="study-mission-btn"
                    onClick={async () => {
                      console.log('🚀 [Botão Visualizar] Clicado para task:', task.id);
                      try {
                        const token = localStorage.getItem('access_token');
                        const url = `${API_URL}/study-missions/by-task/${task.id}`;
                        console.log('   URL:', url);
                        const response = await fetch(url, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        console.log('   Status:', response.status);
                        const data = await response.json();
                        if (data.study_mission) {
                          navigate(`/child/study-missions/${data.study_mission.id}?readonly=true`);
                        } else {
                          alert('Missão de estudo não encontrada');
                        }
                      } catch (err) {
                        console.error('Erro ao abrir missão:', err);
                        alert('Erro ao abrir missão de estudo');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      marginTop: '10px',
                      transition: 'all 0.3s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
                  >
                    👁️ Revisar Missão Concluída
                  </button>
                )}

                {canSubmit && !isCompleted && task.type !== 'STUDY_MISSION' && (
                  <div className="task-input-group">
                    <input
                      id={`file-${task.id}`}
                      type="file"
                      accept="image/*"
                      disabled={loading}
                      onChange={() => handleFileSelected(task.id)}
                    />
                    <label htmlFor={`file-${task.id}`} className="file-label">
                      {selectedTaskId === task.id
                        ? '📸 Foto selecionada'
                        : isRejected
                          ? '📸 Selecionar nova foto'
                          : '📸 Selecionar foto'}
                    </label>
                    <button
                      className="submit-btn"
                      onClick={() => handleSubmitTask(task.id)}
                      disabled={loading}
                    >
                      {loading
                        ? '⏳ Enviando...'
                        : isRejected
                          ? '🔄 Reenviar comprovação'
                          : '✅ Enviar Comprovação'}
                    </button>
                  </div>
                )}
              </div>
            );
          })
          /* eslint-enable no-undef */
        ) : (
          <div className="empty-message">
            <p>{taskFilter === 'pending' ? '🎉 Não há tarefas pendentes para hoje!' : '🎉 Não há tarefas para hoje!'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function RewardsTab({ rewards, onRedeem }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRedeem = async (rewardId) => {
    setLoading(true);
    setError('');

    try {
      await rewardsService.redeemReward(rewardId);
      alert('✅ Recompensa resgatada com sucesso!');
      onRedeem();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao resgatar recompensa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}

      <div className="rewards-grid">
        {rewards.length > 0 ? (
          rewards.map((reward) => (
            <div key={reward.id} className="reward-item">
              <div className="reward-header">
                <h3>{reward.title}</h3>
                <div className="reward-cost">
                  <span className="cost-points">⭐ {reward.cost_points}</span>
                </div>
              </div>

              <p>{reward.description}</p>

              <button
                className="redeem-btn"
                onClick={() => handleRedeem(reward.id)}
                disabled={loading}
              >
                {loading ? '⏳' : '🎁'} Resgatar
              </button>
            </div>
          ))
        ) : (
          <div className="empty-message">
            <p>Nenhuma recompensa disponível</p>
          </div>
        )}
      </div>
    </div>
  );
}

function HistoryTab({ redemptions, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'PENDING_DELIVERY':
        return { emoji: '⏳', text: 'Aguardando entrega', color: '#FF9800' };
      case 'DELIVERED':
        return { emoji: '📦', text: 'Entregue - Confirme!', color: '#2196F3' };
      case 'CHILD_CONFIRMED':
        return { emoji: '✅', text: 'Confirmada', color: '#4CAF50' };
      default:
        return { emoji: '❓', text: 'Desconhecido', color: '#9E9E9E' };
    }
  };

  const handleConfirm = async (redemptionId) => {
    setLoading(true);
    setError('');

    try {
      await rewardsService.confirmRedemption(redemptionId);
      alert('✅ Recompensa confirmada!');
      onConfirm();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao confirmar recompensa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-content">
      {error && <div className="error-message">{error}</div>}

      {redemptions.length > 0 ? (
        <div className="redemptions-list">
          {redemptions.map((redemption) => {
            const statusInfo = getStatusDisplay(redemption.status);
            const reward = redemption.reward;

            return (
              <div key={redemption.id} className="redemption-item">
                <div className="redemption-header">
                  <h3>{reward ? reward.title : 'Recompensa desconhecida'}</h3>
                  <span className="redemption-date">
                    {new Date(redemption.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>

                {reward && <p>{reward.description}</p>}

                <div className="redemption-status" style={{ borderLeftColor: statusInfo.color }}>
                  <span className="status-badge" style={{ backgroundColor: statusInfo.color }}>
                    {statusInfo.emoji} {statusInfo.text}
                  </span>
                </div>

                {redemption.status === 'DELIVERED' && (
                  <button
                    className="confirm-btn"
                    onClick={() => handleConfirm(redemption.id)}
                    disabled={loading}
                  >
                    {loading ? '⏳' : '✅'} Confirmar Recebimento
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-message">
          <p>Você ainda não resgatou nenhuma recompensa</p>
        </div>
      )}
    </div>
  );
}